import { Row, useTranslation } from '@packages/utils';
import { Tooltip } from 'components/Base/Tooltip';
import { InplaceEditLabels } from 'components/Base/EditableFields/components/InplaceEditLabel';
import { useTranslateCapacityShowValue } from 'pages/private/Workshop/hooks/useTranslateCapacityShowValue';
import { ECapacityEmptyTexts } from 'data/types/workshops.types';
import { TAttendanceBadgeProps } from './AttendanceBadge.types';
import { SBadge, SRegistered, SSeparator } from './AttendanceBadge.styles';

// Numeric class capacity, or null for Unlimited / Limited With Workshop.
// Only a numeric capacity can be exceeded, so only then do we warn.
const toNumericCapacity = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const AttendanceBadge = ({ classItem }: TAttendanceBadgeProps) => {
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.workshops.details.sessions.view.attendance-badge',
  });
  const {
    capacity,
    registrantsCount,
    waitlistCount,
    registeredCount,
    attendeesCount,
  } = classItem;

  const capacityText = useTranslateCapacityShowValue(
    capacity.showValue as ECapacityEmptyTexts,
  );
  const numericCapacity = toNumericCapacity(capacity.value);
  // registeredCount already includes the waitlist on purpose (SRD 1.2), which
  // is exactly what makes it exceed capacity — that overflow is the signal.
  const isOverCapacity =
    numericCapacity !== null && registeredCount > numericCapacity;
  const attendedText =
    attendeesCount === null ? t('NOT_TALLIED') : String(attendeesCount);

  return (
    <Row align="middle" wrap={false}>
      <InplaceEditLabels label={t('LABEL')} />
      <Tooltip
        title={t('TOOLTIP', {
          capacity: capacityText,
          registered: registeredCount,
          confirmed: registrantsCount,
          waitlist: waitlistCount,
          attended: attendedText,
        })}
      >
        <SBadge data-testid="attendance-badge">
          <span>{capacityText}</span>
          <SSeparator>{' / '}</SSeparator>
          <SRegistered
            $overCapacity={isOverCapacity}
            data-testid="attendance-badge-registered"
            data-over-capacity={isOverCapacity}
          >
            {registeredCount}
          </SRegistered>
          <SSeparator>{' / '}</SSeparator>
          <span data-testid="attendance-badge-attended">{attendedText}</span>
        </SBadge>
      </Tooltip>
    </Row>
  );
};

export default AttendanceBadge;
